import { ClientAvatar } from './ClientAvatar.js';
import { SESSION_STATUS_LABELS, type Client, type SessionSummary } from '@diana/contracts';
import { ChevronRight, Plus, Mail, Phone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { EmptyState, CollectionSearch, Select, Badge } from '../../ui/index.js';
import { clientActivity, type ClientActivityGroup } from './client-activity.js';
import { InstagramIcon as Instagram } from './InstagramIcon.js';
import './ClientsList.css';

const groups: readonly { id: ClientActivityGroup; title: string }[] = [
  { id: 'requests', title: 'New requests' },
  { id: 'progress', title: 'In progress' },
  { id: 'past', title: 'Past clients' },
  { id: 'other', title: 'Other clients' },
];

export function ClientsList({
  clients,
  shoots,
}: {
  clients: readonly Client[];
  shoots: readonly SessionSummary[];
}) {
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<'all' | ClientActivityGroup>('all');
  const rows = useMemo(() => {
    const now = Date.now();
    return clients
      .map((client) => ({ client, ...clientActivity(client.id, shoots, now) }))
      .sort((a, b) => stageRank(a.stage) - stageRank(b.stage));
  }, [clients, shoots]);
  const matches = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return rows.filter(({ client, group }) => {
      if (activity !== 'all' && activity !== group) return false;
      return (
        !query ||
        [client.name, client.email, client.phone, client.instagram].some((value) =>
          value?.toLocaleLowerCase().includes(query),
        )
      );
    });
  }, [rows, search, activity]);

  return (
    <article className="clients-list">
      <header className="clients-list__heading">
        <div className="clients-list__title">
          <h1>Clients</h1>
          <span>{clients.length} total</span>
        </div>
        <div className="clients-list__toolbar">
          <Link
            className="clients-list__add"
            to="/crm/clients/new"
            aria-label="Add client"
            title="Add client"
          >
            <Plus size={20} aria-hidden="true" />
            <span>Add client</span>
          </Link>
          <CollectionSearch
            className="clients-list__search"
            label="Search clients"
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <Select
            wrapperClassName="clients-list__filter-wrap"
            className="clients-list__filter"
            aria-label="Filter clients"
            value={activity}
            onChange={(event) => setActivity(event.currentTarget.value as typeof activity)}
          >
            <option value="all">All clients</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.title}
              </option>
            ))}
          </Select>
        </div>
      </header>
      {matches.length > 0 && (
        <div className="clients-list__columns" aria-hidden="true">
          <span>Client</span>
          <span>Contact</span>
          <span>Shoots</span>
          <span>Next / last</span>
          <span />
        </div>
      )}
      <ul className="clients-list__items" aria-label="Clients list">
        {matches.map(({ client, stage, count, giftPurchases, date, dateLabel }) => {
          const contacts = [
            { label: 'Instagram', value: client.instagram, Icon: Instagram },
            { label: 'Email', value: client.email, Icon: Mail },
            { label: 'Phone', value: client.phone, Icon: Phone },
          ].filter((item) => item.value?.trim());
          const [contact, ...otherContacts] = contacts;
          const dateContext =
            dateLabel === 'Next scheduled'
              ? 'Upcoming'
              : date && Date.parse(date) > Date.now()
                ? 'Shoot'
                : 'Last shoot';
          return (
            <li key={client.id}>
              <Link className="clients-list__row" to={`/crm/clients/${client.id}`}>
                <span className="clients-list__identity">
                  <ClientAvatar
                    id={client.id}
                    name={client.name}
                    className="clients-list__initials"
                  />
                  <span className="clients-list__name">
                    <strong title={client.name}>{client.name}</strong>{' '}
                    {stage ? (
                      <span className="clients-list__stage" data-stage={stage}>
                        <Badge>{SESSION_STATUS_LABELS[stage]}</Badge>
                      </span>
                    ) : null}
                  </span>
                </span>
                {contact ? (
                  <span className="clients-list__contact">
                    <span
                      className="clients-list__primary-contact"
                      title={`${contact.label}: ${contact.value}`}
                    >
                      <contact.Icon size={15} aria-hidden="true" />
                      <span>{contact.value}</span>
                    </span>
                    {otherContacts.length ? (
                      <span className="clients-list__other-contacts">
                        {otherContacts.map(({ label, value, Icon }) => (
                          <span
                            key={label}
                            role="img"
                            aria-label={`${label} available`}
                            title={`${label}: ${value}`}
                          >
                            <Icon size={14} aria-hidden="true" />
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                <span className="clients-list__metadata" data-empty={count === 0 || undefined}>
                  <span className="clients-list__count">
                    <span>
                      {count} {count === 1 ? 'shoot' : 'shoots'}
                      {giftPurchases
                        ? ` · ${giftPurchases} gift${giftPurchases === 1 ? '' : 's'}`
                        : ''}
                    </span>
                  </span>
                  {date ? (
                    <span className="clients-list__date">
                      <span>{dateContext}</span>{' '}
                      <time dateTime={date}>{formatClientDate(date)}</time>
                    </span>
                  ) : null}
                </span>
                <ChevronRight className="clients-list__arrow" size={16} aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
      {clients.length === 0 ? (
        <EmptyState
          title="No clients yet"
          description="Clients created from website enquiries will appear here."
        />
      ) : matches.length === 0 ? (
        <EmptyState
          title="No matching clients"
          description="Try another search or filter."
          action={
            <button
              className="clients-list__clear"
              type="button"
              onClick={() => {
                setSearch('');
                setActivity('all');
              }}
            >
              Clear filters
            </button>
          }
        />
      ) : null}
    </article>
  );
}

function formatClientDate(value: string) {
  const date = new Date(value);
  const year = new Intl.DateTimeFormat('en-GB', { year: 'numeric', timeZone: 'Europe/Lisbon' });
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/Lisbon',
    ...(year.format(date) !== year.format(new Date()) ? { year: 'numeric' as const } : {}),
  }).format(date);
}

function stageRank(stage: SessionSummary['status'] | null) {
  const stages = ['NewRequest', 'InDiscussion', 'Scheduled', 'Editing'];
  const rank = stage ? stages.indexOf(stage) : -1;
  return rank < 0 ? stages.length : rank;
}
